import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import { hasFinePointer, prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import { SplitChars } from '@/components/motion/split-text'
import Img from '@/components/generals/img'
import { editorial, media } from '@/constants/media'

const FRAMES = [
    { src: media.concreteArcCoat, alt: "A model in a cream rain coat walking beneath a curved concrete roof", wide: true },
    { src: editorial.allBlackPortrait, alt: "A model in an all-black outfit against a plain backdrop", wide: false },
    { src: media.ledgeOvercast, alt: "A model in black seated on a concrete ledge under an overcast sky", wide: true },
    { src: editorial.concreteCourtyardPair, alt: "Two models in long black coats standing between tall concrete walls", wide: false },
    { src: media.concreteStepsSeated, alt: "A model in a black shell and cargo trousers seated on concrete steps", wide: true },
    { src: editorial.leatherJacketStudio, alt: "A model in a leather jacket, flared jeans and a wide hat under studio lights", wide: false },
    { src: media.concreteRoomBlack, alt: "A model in an all-black outfit standing in a sunlit concrete room", wide: true },
    { src: editorial.leatherJacketWall, alt: "A man in a black leather jacket and sunglasses leaning on a wall", wide: false },
    { src: editorial.darkCoatWalk, alt: "A man in a dark coat and pinstriped trousers mid-stride", wide: false },
    { src: editorial.washedDenimLook, alt: "A model in a washed denim jacket and jeans with white sneakers", wide: false },
    { src: editorial.translucentRainCoat, alt: "A model walking in a translucent hooded rain coat", wide: false },
    { src: editorial.flaredDenimLook, alt: "Dark flared jeans worn with a white shirt and ankle boots", wide: false }
]

/** "Move to explore": campaign frames bloom along the cursor path (and play on their own on touch screens). */
const CampaignTrail: FC = (): ReactNode => {
    const section = useRef<HTMLElement>(null)
    const layer = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const sectionEl = section.current
        const layerEl = layer.current

        if (!sectionEl || !layerEl || prefersReducedMotion()) return

        const items = Array.from(layerEl.children) as Array<HTMLElement>
        const last = { x: -999, y: -999 }
        let index = 0
        let depth = 1
        let inView = false
        let autoplay: gsap.core.Tween | null = null

        const spawn = (x: number, y: number): void => {
            const item = items[index % items.length]

            index += 1
            depth += 1
            gsap.killTweensOf(item)
            gsap.set(item, { x, y, xPercent: -50, yPercent: -50, zIndex: depth, autoAlpha: 1, scale: 0.35, rotate: gsap.utils.random(-9, 9) })
            gsap.timeline()
                .to(item, { scale: 1, duration: 0.7, ease: 'expo.out' })
                .to(item, { autoAlpha: 0, scale: 0.82, yPercent: -38, duration: 0.8, ease: 'power2.in' }, 0.7)
        }

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse') return

            const rect = sectionEl.getBoundingClientRect()
            const x = event.clientX - rect.left
            const y = event.clientY - rect.top
            const threshold = Math.max(70, rect.width * 0.06)

            if (Math.hypot(x - last.x, y - last.y) < threshold) return

            last.x = x
            last.y = y
            spawn(x, y)
        }

        const onTap = (event: PointerEvent): void => {
            if (event.pointerType === 'mouse') return

            const rect = sectionEl.getBoundingClientRect()

            spawn(event.clientX - rect.left, event.clientY - rect.top)
        }

        const loop = (): void => {
            if (inView) {
                const rect = sectionEl.getBoundingClientRect()

                spawn(gsap.utils.random(rect.width * 0.18, rect.width * 0.82), gsap.utils.random(rect.height * 0.22, rect.height * 0.78))
            }

            autoplay = gsap.delayedCall(0.55, loop)
        }

        const observer = new IntersectionObserver(([entry]) => {
            inView = entry.isIntersecting
        })

        observer.observe(sectionEl)
        sectionEl.addEventListener('pointermove', onMove)
        sectionEl.addEventListener('pointerdown', onTap)

        if (!hasFinePointer()) loop()

        return () => {
            observer.disconnect()
            sectionEl.removeEventListener('pointermove', onMove)
            sectionEl.removeEventListener('pointerdown', onTap)
            autoplay?.kill()
            items.forEach(item => gsap.killTweensOf(item))
        }
    }, [])

    return (
        <section
            id='campaign'
            ref={section}
            aria-label='SS26 campaign archive'
            className='relative isolate flex h-[100svh] min-h-[560px] items-center justify-center overflow-hidden bg-ink'
        >
            <div className='absolute inset-x-0 top-0 flex items-center justify-between gap-4 pt-[calc(var(--header-height)+24px)] gutter'>
                <p className='label-mono text-acid'>
                    (03) — SS26 Campaign Archive
                </p>

                <p className='label-mono text-ash'>
                    {FRAMES.length} frames
                </p>
            </div>

            <div ref={layer} aria-hidden='true' className='pointer-events-none absolute inset-0 isolate'>
                {FRAMES.map(frame => (
                    <div
                        key={frame.src}
                        className={cn(
                            'invisible absolute left-0 top-0 overflow-hidden opacity-0',
                            frame.wide ? 'aspect-[16/9] w-[clamp(210px,24vw,400px)]' : 'aspect-[3/4] w-[clamp(130px,15vw,250px)]'
                        )}
                    >
                        <Img src={frame.src} alt={frame.alt} sizes={frame.wide ? '400px' : '250px'} />
                    </div>
                ))}
            </div>

            <h2
                data-reveal='chars'
                className='pointer-events-none relative z-10 px-4 text-center font-heading text-[clamp(52px,9vw,150px)] leading-[0.84] text-bone mix-blend-difference'
            >
                <SplitChars text='Move to explore' />
            </h2>

            <p className='absolute inset-x-0 bottom-10 text-center label-mono text-ash'>
                <span className='hidden lg:inline'>Move your cursor across the frame</span>
                <span className='lg:hidden'>Tap anywhere to pull a frame</span>
            </p>
        </section>
    )
}

export default CampaignTrail
